/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { useItamTenantContext } from '@jira/itam__tenant-context/src/useItamTenantContext.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { useShowCustomerService } from '@jira/servicedesk__customer-service-common/src/useShowCustomerService.tsx';

import { useIsCrossProjectViewsEnabled } from '@jira/polaris__lib-entitlement-utils/src/index.tsx';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isIsolatedCloud } from '@atlassian/atl-context';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type JiraConfigurableNavigationItem,
	L1_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useShowAssets } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-assets/src/common/use-show-assets/index.tsx';
import { useNavigationPlansVisibility } from '@jira/portfolio__services-plans-item-visibility/src/useNavigationViewPlansPermission.tsx';
import { useShowCustomers } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-customers/src/controllers/use-show-customers/index.tsx';
import { useGlobalApps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/global-apps/index.tsx';

/*
  filtering out menu items:
  - if JSM and consolidated from opsgenie -> show "Operations"
  - if JSM and premium -> show "Assets"
  - if JPD and premium -> show "Roadmaps"
  - if any JSW is present -> show "Plans" (its inner logic will handle whether to show the menu or an upsell prompt)
  - if JSM and premium -> show "Communication"
*/
export const useFilterL1MenuItemsByEditionAndLicense = (
	navItems: readonly JiraConfigurableNavigationItem[],
	params?: { shouldShowRoadmapsUpsell: boolean },
): JiraConfigurableNavigationItem[] => {
	const showAssets = useShowAssets();
	const { shouldUseJSM } = useOgTenantInfo();
	const shouldShowRoadmaps = useIsCrossProjectViewsEnabled();
	const shouldShowRoadmapsUpsell = params?.shouldShowRoadmapsUpsell;
	const [shouldRenderPlans] = useNavigationPlansVisibility();
	const showCustomerService = useShowCustomerService();
	const showCustomers = useShowCustomers();
	const { goals, assets, confluence, projects, loom, teams, opsgenie } = useGlobalApps();
	const shouldRenderApps = fg('use_frontend_feature_encoding_sdk_remaining')
		? isFeatureEnabled('jira', 'jira-apps-menu')
		: !(fg('jira-hide-apps-in-isolated-cloud') && isIsolatedCloud());
	const itamContext = useItamTenantContext();

	return useMemo(
		() =>
			navItems.filter(({ menuId }) => {
				if (menuId === L1_MENU_ID.OPERATIONS) {
					return shouldUseJSM;
				}

				if (menuId === L1_MENU_ID.ASSETS) {
					/* remove this on
					 * assets_as_an_app_v2 cleanup &&
					 *
					 * Should hide the L1 Assets menu item when the asset shortcut is present
					 */
					return showAssets && !assets;
				}

				if (menuId === L1_MENU_ID.ROADMAPS) {
					return shouldShowRoadmaps || shouldShowRoadmapsUpsell;
				}

				if (menuId === L1_MENU_ID.PLANS) {
					return shouldRenderPlans;
				}

				if (menuId === L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS) {
					// Filter out Customer Service Analytics.
					// It is always nested under Dashboards, but may exist in legacy user configurations from before this change.
					return false;
				}

				// See https://jplat.jira.atlassian.cloud/browse/BLU-3307
				// For the Dogfooding phase of Nav4 we are going to intentionally hide the Goals menu item.
				// This if block should be removed when we work on https://jplat.jira.atlassian.cloud/browse/BLU-3261
				// as we will know the correct URL for the Goals menu item at that point.
				if (menuId === L1_MENU_ID.GOALS) {
					return false;
				}

				if (menuId === L1_MENU_ID.CUSTOMER_SERVICE) {
					return showCustomerService;
				}

				if (menuId === L1_MENU_ID.CUSTOMERS) {
					return showCustomers;
				}

				if (menuId === L1_MENU_ID.GLOBAL_APP_ASSETS) {
					return assets;
				}

				if (menuId === L1_MENU_ID.GLOBAL_APP_GOALS) {
					return goals;
				}

				// App Shortcuts for Platform Component, feature gated in useGlobalApps
				if (menuId === L1_MENU_ID.GLOBAL_APP_CONFLUENCE) {
					return confluence;
				}
				if (menuId === L1_MENU_ID.GLOBAL_APP_PROJECTS) {
					return projects;
				}
				if (menuId === L1_MENU_ID.GLOBAL_APP_LOOM) {
					return loom;
				}
				if (menuId === L1_MENU_ID.GLOBAL_APP_TEAMS) {
					return teams;
				}
				if (menuId === L1_MENU_ID.GLOBAL_APP_OPSGENIE) {
					return opsgenie;
				}
				if (menuId === L1_MENU_ID.APPS) {
					return shouldRenderApps;
				}

				if (menuId === L1_MENU_ID.ITAM) {
					// return false when FG is off to exclude it from navItems
					return fg('itam_ftw') ? itamContext.ham.shouldShowItam : false;
				}

				return true;
			}),
		[
			navItems,
			shouldUseJSM,
			showAssets,
			shouldShowRoadmaps,
			shouldShowRoadmapsUpsell,
			shouldRenderPlans,
			showCustomerService,
			goals,
			assets,
			confluence,
			projects,
			loom,
			teams,
			opsgenie,
			shouldRenderApps,
			showCustomers,
			itamContext.ham.shouldShowItam,
		],
	);
};
